import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children"],
  _excluded2 = ["children", "inline", "ordered", "styled", "use", "aria-label", "childStyles", "firstChildStyles", "lastChildStyles", "wrapChildren"],
  _excluded3 = ["display"];
import styled from 'foundations-theming/utils';
import { ListContextProvider, useListContext } from './ListContext';
import { getListItemStyles, getListStyles } from './List.helpers';
import { Flex } from '../../../components/Flex/Flex';
import { View } from '../../../components/View/View';
import { Children } from 'react';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledVerticalSeparator = styled.span.withConfig({
  displayName: "List__StyledVerticalSeparator",
  componentId: "f6qme6-0"
})(({
  theme
}) => {
  return {
    inlineSize: '1px',
    blockSize: '1.1rem',
    backgroundColor: theme.color['border-core-default'],
    '&:first-child': {
      display: 'none'
    }
  };
});
export const ListItem = _ref => {
  let {
      children
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    use,
    inline
  } = useListContext();
  const listItemStyleProps = getListItemStyles(use, inline);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [use === 'inline-divided' ? /*#__PURE__*/_jsx(StyledVerticalSeparator, {}) : null, /*#__PURE__*/_jsx(View, Object.assign({}, listItemStyleProps, {
      as: "li"
    }, rest, {
      children: children
    }))]
  });
};
export const List = _ref2 => {
  let {
      children,
      inline = false,
      ordered = false,
      styled: styledProp = false,
      use = 'default',
      'aria-label': ariaLabel,
      childStyles,
      firstChildStyles,
      lastChildStyles,
      wrapChildren = false
    } = _ref2,
    rest = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  const listElement = ordered ? 'ol' : 'ul';
  const filteredChildren = Children.toArray(children).filter(child => child != null);

  /**
   * If wrapChildren is true, map over the children and wrap them in ListItem
   * and apply childStyles, firstChildStyles, or lastChildStyles to the wrapper.
   */
  const computedChildren = !wrapChildren ? filteredChildren : Children.map(filteredChildren, (child, index) => {
    /**
     * Imitate the behavior of the childClassName, firstChildClassName, and lastChildClassName in UIList.
     * Do not merge styles between the childStyles, firstChildStyles, and lastChildStyles.
     */
    let computedChildrenStyles = {};
    if (index === 0 && firstChildStyles) {
      computedChildrenStyles = firstChildStyles;
    } else if (index === Children.count(children) - 1 && lastChildStyles) {
      computedChildrenStyles = lastChildStyles;
    } else if (childStyles) {
      computedChildrenStyles = childStyles;
    }
    return /*#__PURE__*/_jsx(ListItem, Object.assign({}, computedChildrenStyles, {
      children: child
    }), index);
  });
  if (inline || use === 'inline-divided') {
    return /*#__PURE__*/_jsx(ListContextProvider, {
      use: use,
      inline: inline,
      children: /*#__PURE__*/_jsx(Flex, Object.assign({}, rest, {
        as: listElement,
        display: inline ? 'inline-flex' : 'flex',
        flexDirection: "row",
        flexWrap: "wrap",
        columnGap: "200",
        alignItems: "center",
        "aria-label": ariaLabel,
        children: computedChildren
      }))
    });
  }
  const listStyleProps = getListStyles(styledProp);
  const restProps = _objectWithoutPropertiesLoose(rest, _excluded3);
  return /*#__PURE__*/_jsx(ListContextProvider, {
    use: use,
    children: /*#__PURE__*/_jsx(View, Object.assign({}, listStyleProps, restProps, {
      as: listElement,
      "aria-label": ariaLabel,
      children: computedChildren
    }))
  });
};